import { Select } from '@app/form_components';
import { useServices } from '@app/hooks';
import { Design, Option } from '@app/types';
import { compareDesignVersions, CompareResult } from '@app/utilities';
import { FC, useEffect, useState } from 'react';

function versionToOption({ version }: { version: string; }): Option<string> {
  return {
    label: version,
    value: version
  };
}

export type VersionSelectorProps = {
  branchId: string;
  onVersionChange: (newVersion: Design) => Promise<void> | void;
  /** Whether versions should be fetch before the user clicks the component. */
  eagerLoad?: boolean;
  version: string;
};

export const VersionSelector: FC<VersionSelectorProps> = ({
  branchId,
  onVersionChange,
  eagerLoad,
  version
}) => {
  const {
    avsService,
    loadingService
  } = useServices();
  const [isLoading, setIsLoading] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string>();
  const [options, setOptions] = useState<Option<string>[]>();

  const downloadVersions = async (forceDownload = false) => {
    if (isLoading || (!forceDownload && options)) {
      return;
    }
    setIsLoading(true);
    const versions = await avsService.getVersions(branchId);
    setLatestVersion(versions[0]?.version);
    setOptions(versions.map(versionToOption));
    setIsLoading(false);
  };

  useEffect(() => {
    if (eagerLoad) {
      downloadVersions();
      return;
    }

    (async () => {
      const latest = await avsService.getLatestVersion(branchId);
      setLatestVersion(latest?.version);
    })();
  }, [eagerLoad]);

  useEffect(() => {
    const newLatestVersion = (
      latestVersion
      && version
      && compareDesignVersions(version, latestVersion) === CompareResult.aIsLarger
    );
    if (newLatestVersion) {
      downloadVersions(true);
    }
  }, [version, latestVersion]);

  return (
    <Select
      isLoading={isLoading}
      onChange={async (newVersion) => {
        if (!newVersion) {
          return;
        }

        ++loadingService.loadingCount;
        const serialized = await avsService.getDesign(branchId, newVersion);
        await onVersionChange(serialized);
        --loadingService.loadingCount;
      }}
      onFocus={() => downloadVersions()}
      options={options ?? [versionToOption({ version })]}
      styles={{
        singleValue: (styles, { data }) => {
          return (latestVersion && latestVersion !== data.value) ? (
            // Add dot if there is a newer version.
            Select.styleAddDot(styles, 'var(--color-primary)')
          ) : (
            styles
          );
        }
      }}
      value={version}
    />
  );
};
